/**
 * Descoberta de plugins e de pacotes opcionais.
 *
 * Duas razoes para isto existir em vez de `import { financePlugin } from ...`:
 *
 *  1. **Regra 1.** A casca nao pode conter o nome de nenhum dominio de negocio.
 *     Aqui a lista de plugins e o conteudo da pasta `plugins/`, nao uma lista
 *     escrita a mao que alguem tem de se lembrar de atualizar.
 *
 *  2. **Arranque resiliente.** Um pacote que ainda nao existe, ou um plugin que
 *     quebra ao ser importado, tem de dar uma linha no diagnostico e nada mais.
 *     Com `import` estatico, o mesmo caso da tela branca.
 *
 * `import.meta.glob` resolve os caminhos em tempo de compilacao e devolve um
 * mapa vazio quando nao ha arquivos — nao ha aqui nenhum caminho magico que
 * possa apontar para o vazio sem se notar.
 */
import type { FlosPlugin } from '@flos/shared';
import type {
  AiModulePort,
  AuthModulePort,
  NotificationsModulePort,
  SyncModulePort,
} from './ports';

type Loader = () => Promise<unknown>;

/** Pacotes da plataforma que a casca usa se existirem e dispensa se nao. */
const OPTIONAL_PACKAGES: Record<string, Loader> = import.meta.glob(
  '../../../../packages/{ai,auth,notifications,permissions,sync}/src/index.ts',
);

/** Todos os plugins presentes na monorepo. Sem lista escrita a mao. */
const PLUGIN_MODULES: Record<string, Loader> = import.meta.glob(
  '../../../../plugins/*/src/index.ts',
);

function packageLoader(name: string): Loader | null {
  const suffix = `/packages/${name}/src/index.ts`;
  for (const [path, loader] of Object.entries(OPTIONAL_PACKAGES)) {
    if (path.endsWith(suffix)) return loader;
  }
  return null;
}

/** Nome da pasta do plugin, para o diagnostico dizer qual falhou. */
function pluginNameFromPath(path: string): string {
  const match = /\/plugins\/([^/]+)\/src\/index\.ts$/.exec(path);
  return match?.[1] ?? path;
}

export interface DiscoveryProblem {
  /** Pasta do plugin ou nome do pacote. */
  source: string;
  reason: string;
}

export interface PluginDiscovery {
  plugins: FlosPlugin[];
  problems: DiscoveryProblem[];
}

function looksLikePlugin(value: unknown): value is FlosPlugin {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { manifest?: unknown; register?: unknown };
  return (
    typeof candidate.register === 'function' &&
    typeof candidate.manifest === 'object' &&
    candidate.manifest !== null &&
    typeof (candidate.manifest as { id?: unknown }).id === 'string'
  );
}

/**
 * Importa todos os plugins encontrados. Em paralelo de proposito: sao pequenos e
 * o arranque tem um orcamento de 2 segundos.
 */
export async function discoverPlugins(): Promise<PluginDiscovery> {
  const entries = Object.entries(PLUGIN_MODULES);
  const problems: DiscoveryProblem[] = [];
  const plugins: FlosPlugin[] = [];

  const loaded = await Promise.all(
    entries.map(async ([path, load]) => {
      try {
        return { path, module: await load() };
      } catch (error) {
        return { path, error };
      }
    }),
  );

  for (const result of loaded) {
    const name = pluginNameFromPath(result.path);
    if ('error' in result) {
      problems.push({
        source: name,
        reason: result.error instanceof Error ? result.error.message : String(result.error),
      });
      continue;
    }
    const module = result.module as Record<string, unknown>;
    const candidate =
      (looksLikePlugin(module['default']) ? module['default'] : null) ??
      Object.values(module).find(looksLikePlugin) ??
      null;

    if (!candidate) {
      problems.push({
        source: name,
        reason: 'o modulo nao exporta nenhum plugin com manifesto e register()',
      });
      continue;
    }
    plugins.push(candidate);
  }

  // Ordem estavel: o registro ordena por dependencias, mas um arranque
  // reprodutivel torna os relatorios comparaveis entre execucoes.
  plugins.sort((a, b) => a.manifest.id.localeCompare(b.manifest.id, 'pt'));
  return { plugins, problems };
}

async function loadOptional<T>(
  name: string,
  shape: (module: Record<string, unknown>) => T | null,
): Promise<T | null> {
  const loader = packageLoader(name);
  if (!loader) return null;
  try {
    const module = (await loader()) as Record<string, unknown>;
    return shape(module);
  } catch {
    // Um pacote opcional partido nao pode travar o arranque. Quem chamou decide
    // o que dizer a pessoa (ex. "o assistente nao esta disponivel").
    return null;
  }
}

export function loadAiModule(): Promise<AiModulePort | null> {
  return loadOptional<AiModulePort>('ai', (module) => {
    if (typeof module['IntentRouter'] !== 'function') return null;
    if (typeof module['createAiProvider'] !== 'function') return null;
    return module as unknown as AiModulePort;
  });
}

export function loadAuthModule(): Promise<AuthModulePort | null> {
  return loadOptional<AuthModulePort>('auth', (module) => {
    // Precisamos das duas pecas: a fabrica do servico e o adaptador do
    // fornecedor. Com so uma delas nao ha entrada nenhuma a construir.
    if (typeof module['createAuthService'] !== 'function') return null;
    if (typeof module['GoogleAuthProvider'] !== 'function') return null;
    return module as unknown as AuthModulePort;
  });
}

export function loadSyncModule(): Promise<SyncModulePort | null> {
  return loadOptional<SyncModulePort>('sync', (module) =>
    typeof module['SyncEngine'] === 'function' ? (module as unknown as SyncModulePort) : null,
  );
}

export function loadNotificationsModule(): Promise<NotificationsModulePort | null> {
  return loadOptional<NotificationsModulePort>('notifications', (module) =>
    typeof module['createNotifier'] === 'function'
      ? (module as unknown as NotificationsModulePort)
      : null,
  );
}
